import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * ErrorBoundary — catches any React render error in the subtree and shows a
 * friendly recovery screen instead of a blank white page. Prevents the
 * "blank on production" class of bugs where a silent runtime error kills the
 * entire route render.
 *
 * Usage:
 *   <ErrorBoundary route="/requests"><BookingRequests /></ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", this.props.route || "unknown", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "Unexpected error";
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center bg-slate-50 p-6" data-testid="error-boundary">
        <div className="max-w-md w-full text-center rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_20px_60px_-24px_rgba(200,163,77,0.35)]">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#FBF3DA] border border-[#C8A34D]/40 grid place-items-center mb-4">
            <AlertTriangle className="w-7 h-7 text-[#A6852F]" />
          </div>
          <h1 className="font-head text-2xl font-black text-slate-900">Something went wrong</h1>
          <p className="text-sm text-slate-600 mt-2 font-body">
            We hit a bump loading this page. Please refresh — if it keeps happening, try going home and returning.
          </p>
          <p className="text-[11px] text-slate-400 mt-3 font-mono break-words">{msg}</p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <Button onClick={this.reset} className="ss-btn ss-btn-primary" data-testid="error-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button asChild variant="outline" className="ss-btn ss-btn-ghost border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="error-home-btn">
              <Link to="/"><Home className="w-4 h-4 mr-2" /> Go home</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
